import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil } from 'lucide-react';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableRow } from '../../ui/table';
import { useToast } from '../../ui/use-toast';
import { cn } from '../../../lib/utils';
import { createSerialQueue } from '../../../lib/serial-queue';
import { roundRefreshOptions, useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { competitionRoundsService } from '../../../services/competition-rounds';
import type { CompetitionParticipant } from '../../../types/competition';
import type { CreateLeadResultData, LeadRoundResult, RoundResult } from '../../../types/competition-round';
import type { LeadCardDraft } from '../LeadCardEditor';
import { LeadCardSummary } from '../LeadCardSummary';
import { LeadScoringDialog } from '../LeadScoringDialog';
import { RefreshFlash } from '../RefreshFlash';
import {
  RefreshGenerationContext,
  leadCardSignature,
  useRefreshGeneration,
} from '../refresh-flash';
import { permittedLeadStyles } from '../lead-climb-style';
import {
  findResultForParticipant,
  getParticipantDisplayText,
  getParticipantName,
  getParticipantSurname,
  getParticipantTeam,
} from '../participantUtils';
import { JudgeDoneDivider } from './JudgeDoneDivider';
import { JudgeSearch, JudgeSortHeader } from './JudgeFiltersControls';
import { arrangeParticipants, type JudgeTableProps } from './judge-blocks';

/**
 * One lead route, one class: the judge's own screen.
 *
 * The same shape as the boulder table — one row per athlete, tap to open the one
 * being scored — and the difference is the only one the disciplines have: a lead
 * card is **two numbers written once the climber comes off the wall**. So a row
 * opens the whole climb (`LeadScoringDialog`): the clock, the route's drawing,
 * the two numbers. One go per route, so a card is replaced rather than added to,
 * and an athlete with a card sinks to the bottom.
 */
export function JudgeLeadTable({
  competitionSlug,
  round,
  roundClimb,
  participants,
  filters,
  onFiltersChange,
  scoringOpen,
}: JudgeTableProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [results, setResults] = useState<RoundResult[]>([]);
  // Nothing is drawn before the first answer: a table sorted without its
  // results would put the finished athletes on top, then move them.
  const [ready, setReady] = useState(false);
  /** The one athlete whose card is open, by entry id. */
  const [scoring, setScoring] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  // Each athlete's saves one after another, and what tells the periodic refresh
  // that one went out while it was reading (see SerialQueue#quietSince).
  const runSerially = useRef(createSerialQueue()).current;

  /**
   * The route's cards as the server has them. `unlessBusy` is the periodic
   * refresh's: its answer is dropped if a save went out while it was being read,
   * since it may be from before it. A save's own reload, which runs after the
   * save, always lands.
   */
  // Bumped by the periodic refresh only, so a card somebody else wrote lights
  // up and the one this judge just saved does not.
  const [generation, bumpGeneration] = useRefreshGeneration();

  const refreshResults = useCallback(
    async ({ unlessBusy = false }: { unlessBusy?: boolean } = {}) => {
      const mark = runSerially.mark();
      try {
        const fetched = await competitionRoundsService.getRoundClimbResults(competitionSlug, round.id, roundClimb.id);
        if (unlessBusy && !runSerially.quietSince(mark)) return;
        setResults(fetched);
        if (unlessBusy) bumpGeneration();
      } catch {
        // Silent refresh failure: the table on screen is still the one that counts.
      } finally {
        setReady(true);
      }
    },
    [competitionSlug, round.id, roundClimb.id, runSerially, bumpGeneration]
  );

  useEffect(() => {
    refreshResults();
  }, [refreshResults]);

  // At the competition's pace, while the round takes marks.
  useAutoRefresh(() => refreshResults({ unlessBusy: true }), roundRefreshOptions(round));

  const cardFor = (participant: CompetitionParticipant) =>
    findResultForParticipant(participant, results) as LeadRoundResult | undefined;

  const arranged = arrangeParticipants(participants, filters, (p) => !!cardFor(p));

  /**
   * Writes one athlete's card: a create the first time, an overwrite afterwards.
   * One go per route, so there is nothing to accumulate and no order to keep.
   */
  const saveCard = (participant: CompetitionParticipant, draft: LeadCardDraft) =>
    runSerially(participant.id, async () => {
      setSaving(participant.id);
      try {
        const existing = cardFor(participant);
        const payload: CreateLeadResultData = {
          holds_count: draft.holds,
          time_seconds: draft.timeSeconds ?? undefined,
          // Sent whenever the dialog has one: where the class climbs one way only
          // the dialog already carries it, and where it may do either the save
          // waited for the judge to say.
          climbed_as: draft.style ?? undefined,
        };

        if (existing) {
          await competitionRoundsService.updateResult(competitionSlug, round.id, roundClimb.id, existing.id, payload);
        } else {
          await competitionRoundsService.createLeadResult(competitionSlug, round.id, roundClimb.id, {
            ...payload,
            participant_id: participant.id,
          });
        }

        await refreshResults();
        setScoring(null);
      } catch (error) {
        toast({
          title: t('errors.actionFailed', { defaultValue: 'Action failed' }),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
      } finally {
        setSaving(null);
      }
    });

  const clearCard = (participant: CompetitionParticipant) =>
    runSerially(participant.id, async () => {
      const existing = cardFor(participant);
      if (!existing) return;

      setSaving(participant.id);
      try {
        await competitionRoundsService.deleteResult(competitionSlug, round.id, roundClimb.id, existing.id);
        await refreshResults();
        setScoring(null);
      } catch (error) {
        toast({
          title: t('errors.actionFailed', { defaultValue: 'Action failed' }),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
      } finally {
        setSaving(null);
      }
    });

  const scoringParticipant = participants.find((p) => p.id === scoring) ?? null;
  const scoringCard = scoringParticipant ? cardFor(scoringParticipant) : undefined;

  /**
   * Top rope, lead, or the athlete's own choice on this route — read from
   * **their** class, since the card belongs to the athlete. The route's own
   * forcing wins over both (`permittedLeadStyles`).
   */
  const scoringStyles = permittedLeadStyles(
    roundClimb,
    scoringParticipant?.competition_category_id,
    round.category_styles
  );

  const open = (participant: CompetitionParticipant) => {
    if (scoringOpen) setScoring(participant.id);
  };

  if (!ready) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <RefreshGenerationContext.Provider value={generation}>
      {/* How many holds there are to get. Everything typed below is read against
          it, so it is said once at the top rather than on every row. */}
      {roundClimb.holds_count != null && (
        <p className="mb-4 text-sm text-muted-foreground" data-testid="route-holds-count">
          {t('leadRounds.holdsOnRoute', { count: roundClimb.holds_count })}
        </p>
      )}

      <JudgeSearch filters={filters} onChange={onFiltersChange} placeholder={t('judgeLead.searchPlaceholder')} />

      <Table>
        <JudgeSortHeader filters={filters} onChange={onFiltersChange} />
        <TableBody>
          {arranged.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="py-6 text-center text-muted-foreground">
                {t('judgeLead.noParticipants')}
              </TableCell>
            </TableRow>
          )}

          {arranged.map((participant, index) => {
            const card = cardFor(participant);
            // The first of the people with a card, who sink to the bottom: the
            // line goes above them, when there is anybody above.
            const firstDone = !!card && index > 0 && !cardFor(arranged[index - 1]);

            return (
              <Fragment key={participant.id}>
                {firstDone && <JudgeDoneDivider />}
                <TableRow
                  className={cn('border-0', card && 'text-muted-foreground', scoringOpen && 'cursor-pointer')}
                  data-state={scoring === participant.id ? 'selected' : undefined}
                  data-testid="participant-row"
                  onClick={() => open(participant)}
                >
                  <TableCell className="py-2 font-medium">{getParticipantName(participant)}</TableCell>
                  <TableCell className="py-2">{getParticipantSurname(participant)}</TableCell>
                  <TableCell className="py-2">{getParticipantTeam(participant)}</TableCell>
                </TableRow>

                <TableRow
                  className={cn(scoringOpen && 'cursor-pointer')}
                  data-state={scoring === participant.id ? 'selected' : undefined}
                  data-testid="card-row"
                  onClick={() => open(participant)}
                >
                  <TableCell colSpan={3} className="pb-2 pt-0">
                    <div className="flex items-center gap-2">
                      <RefreshFlash value={leadCardSignature(card)}>
                        <LeadCardSummary
                          holds={card?.holds_count}
                          timeSeconds={card?.time_seconds}
                          formattedTime={card?.formatted_time}
                          topped={card?.topped}
                          climbedAs={card?.climbed_as}
                        />
                      </RefreshFlash>
                      {card && scoringOpen && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          aria-label={t('leadRounds.editCard')}
                          data-testid="edit-card-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            open(participant);
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      {/* One athlete at a time, from the ground to the card: the clock, then the
          route's photo with its holds circled, then the two numbers. Saving
          closes it and the table behind is where the next athlete is chosen. */}
      <LeadScoringDialog
        open={scoringParticipant !== null}
        onOpenChange={(next) => !next && setScoring(null)}
        athleteName={scoringParticipant ? getParticipantDisplayText(scoringParticipant) : ''}
        routeName={roundClimb.display_name ?? ''}
        imageUrl={roundClimb.image_url}
        holds={roundClimb.holds}
        maxHolds={roundClimb.holds_count}
        cardHolds={scoringCard?.holds_count ?? null}
        cardTimeSeconds={scoringCard?.time_seconds ?? null}
        cardFormattedTime={scoringCard?.formatted_time}
        cardClimbedAs={scoringCard?.climbed_as}
        styles={scoringStyles}
        saving={saving === scoring}
        readOnly={!scoringOpen}
        onSave={(draft) => {
          if (scoringParticipant) return saveCard(scoringParticipant, draft);
        }}
        onClear={
          scoringCard
            ? () => {
                if (scoringParticipant) return clearCard(scoringParticipant);
              }
            : undefined
        }
      />
    </RefreshGenerationContext.Provider>
  );
}
